/**
 * `GET /sso/discovery?email=…` — the only API call this product makes before anybody has signed in.
 *
 * ## It is deliberately not `api()`
 *
 * `api/client.ts` attaches the session bearer and maps our error envelope. There is no session
 * here, and a 429 or an unreachable API must not stop somebody signing in — so this has its own
 * tiny fetch and its own failure rule: ANY failure answers `password`.
 *
 * That rule is the same one the server holds, for the same reason. The login form treating a
 * network blip as "this domain has no SSO" costs one extra click for an SSO user; treating it as
 * an error would strand them on a screen with no password field and nothing to press.
 */
export type Discovery = {
  readonly mode: 'microsoft' | 'saml' | 'password'
  readonly provider_id?: string
  /** Whether to hide the password field. NOT the enforcement — the server is the gate. */
  readonly sso_enforced?: boolean
}

const PASSWORD: Discovery = { mode: 'password' }

export async function discover(email: string, signal?: AbortSignal): Promise<Discovery> {
  const base = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '')
  if (base === undefined || base === '') return PASSWORD
  try {
    const res = await fetch(`${base}/sso/discovery?email=${encodeURIComponent(email)}`, {
      // No credentials, deliberately: there are none, and sending cookies to an endpoint that
      // answers before authentication is how a CSRF surface appears where there was none.
      credentials: 'omit',
      ...(signal !== undefined ? { signal } : {}),
    })
    if (!res.ok) return PASSWORD
    const body: unknown = await res.json()
    if (typeof body !== 'object' || body === null) return PASSWORD
    const mode = (body as { mode?: unknown }).mode
    if (mode !== 'microsoft' && mode !== 'saml' && mode !== 'password') return PASSWORD
    const providerId = (body as { provider_id?: unknown }).provider_id
    const enforced = (body as { sso_enforced?: unknown }).sso_enforced
    return {
      mode,
      ...(typeof providerId === 'string' ? { provider_id: providerId } : {}),
      ...(typeof enforced === 'boolean' ? { sso_enforced: enforced } : {}),
    }
  } catch {
    return PASSWORD
  }
}
